import React, { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { flightAPI, hotelSearchAPI, whatsappAPI, itineraLeadsAPI } from '../../services/api';
import VERSION from '../../version';
import { PAGE_GROUPS, findSidebarGroupKey, canSeeGroupItem } from '../../config/pageGroups'; // V70.65
import { canEnterHRPortal } from '../../config/hrPageGroups'; // V71 — İK CTA, HRLayout ile aynı karar
import useHrPermissions from '../../hooks/useHrPermissions';
import './Sidebar.css';
import IdeaWidget from '../IdeaWidget';

// V71.31.2 — asistan devri uyarı sesi (Web Audio; tarayıcı kuralı: sayfada bir tıklama sonrası çalar)
let _devirCtx = null;
const devirCtx = () => {
  if (!_devirCtx) _devirCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (_devirCtx.state === 'suspended') _devirCtx.resume();
  return _devirCtx;
};
if (typeof window !== 'undefined') {
  const hazirla = () => { try { devirCtx(); } catch { /* ses yok */ } };
  window.addEventListener('click', hazirla, { once: true });
  window.addEventListener('keydown', hazirla, { once: true });
}
function devirSesi(acil) {
  try {
    const ctx = devirCtx();
    const t0 = ctx.currentTime;
    (acil ? [988, 740, 988, 740] : [660, 880, 1175]).forEach((f, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = f;
      const b = t0 + i * 0.18;
      g.gain.setValueAtTime(0.0001, b); g.gain.exponentialRampToValueAtTime(0.2, b + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, b + 0.16);
      o.connect(g); g.connect(ctx.destination); o.start(b); o.stop(b + 0.17);
    });
  } catch { /* ses desteklenmiyor */ }
}

/* ───── SVG Icons (stroke, 16px) ───── */
const icons = {
  home: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  users: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  ticket: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 10h20"/><path d="M7 15h.01"/><path d="M11 15h2"/></svg>,
  hotel: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><line x1="9" y1="9" x2="10" y2="9"/><line x1="9" y1="13" x2="10" y2="13"/><line x1="9" y1="17" x2="10" y2="17"/></svg>,
  clock: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  credit: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  money: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  chart: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  calendar: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  chat: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
  phone: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  upload: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  file: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>,
  building: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><path d="M9 9h1"/><path d="M9 13h1"/><path d="M9 17h1"/></svg>,
  settings: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.604.852.997 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  coins: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><line x1="7" y1="6" x2="7.01" y2="6"/><line x1="9" y1="10" x2="9.01" y2="10"/></svg>,
  passport: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><circle cx="12" cy="10" r="3"/><path d="M7 17h10"/></svg>,
  checkin: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  shield: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  news: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8z"/></svg>,
  bookmark: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>,
  barChart: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  trendUp: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  wallet: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M16 12h.01"/><path d="M2 10h20"/></svg>,
  award: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></svg>,
  gift: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>,
  target: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  send: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  search: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  plane: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/></svg>,
  map: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>,
  layout: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>,
  chevronLeft: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  chevronRight: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
};

/* V70.71 — gradyanlı ikon rozeti: grup satırları grubun rengini (pageGroups.color),
   tekil linkler kendi grad ikilisini kullanır */
const GRAD_FALLBACK = ['#475569', '#64748b'];
const IconChip = ({ icon, grad }) => {
  const [from, to] = grad || GRAD_FALLBACK;
  return (
    <span className="sb-ic" style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}>
      {icon}
    </span>
  );
};

const STORAGE_KEY = 'sidebar-collapsed';
const MOBILE_BP = 768;

const Sidebar = () => {
  const { user, isAdmin, hasPermission } = useAuth();
  const location = useLocation(); // V70.65 — aktif grup tespiti
  // V71 — İK yetki motoru (rol/set/vekalet) portal girişine HRLayout'taki gibi katılır
  const hrPerms = useHrPermissions();
  const hrEngine = useMemo(() => ({ ready: hrPerms.ready, canExplicit: hrPerms.canExplicit }), [hrPerms.ready, hrPerms.canExplicit]);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BP);
  const [mobileOpen, setMobileOpen] = useState(false);

  // V71 — daraltma artık alttaki düğmeyle açılıp kapanır (eski kayıtla kilitli kalma sorunu bitti)
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= MOBILE_BP;
      setIsMobile(mobile);
      if (mobile) setMobileOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    window.__sidebarMobileToggle = () => setMobileOpen(v => !v);
    return () => { delete window.__sidebarMobileToggle; };
  }, []);

  useEffect(() => {
    if (isMobile && mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    // V71 — mobil çekmece açıkken yüzen butonlar (Hızlı Araçlar, Fikrim Var) gizlenir
    document.documentElement.toggleAttribute('data-sidebar-open', isMobile && mobileOpen);
  }, [isMobile, mobileOpen]);

  useEffect(() => {
    if (!isMobile) {
      try { localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0'); } catch {}
    }
    document.documentElement.setAttribute('data-sidebar', isMobile ? 'mobile' : (collapsed ? 'collapsed' : 'expanded'));
  }, [collapsed, isMobile]);

  // FR24 health
  const [huStatus, setHuStatus] = useState(null);
  useEffect(() => {
    let mounted = true;
    const check = () => {
      flightAPI.health()
        .then(r => mounted && setHuStatus(r.data?.status === 'ok' ? 'ok' : 'error'))
        .catch(() => mounted && setHuStatus('error'));
    };
    check();
    const iv = setInterval(check, 120000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  // V70.103 — Otel sağlayıcı canlılığı (uçaktaki desenle aynı): TravelRobot otel
  // kanalı yapılandırılmış ve API yanıt veriyorsa yeşil, aksi halde kırmızı
  const [hotelStatus, setHotelStatus] = useState(null);
  useEffect(() => {
    let mounted = true;
    const check = () => {
      hotelSearchAPI.health()
        .then(r => mounted && setHotelStatus(r.data?.status === 'ok' && r.data?.providers?.travelrobot ? 'ok' : 'error'))
        .catch(() => mounted && setHotelStatus('error'));
    };
    check();
    const iv = setInterval(check, 120000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  // V71.14.8 — Müşteri Hattı okunmamış rozeti: 20 sn yoklama + odak/masa olayında anında
  const canWa = hasPermission('whatsapp', 'view');
  const [waUnread, setWaUnread] = useState(null);
  useEffect(() => {
    if (!canWa) return undefined;
    let mounted = true, t = null;
    const check = () => {
      clearTimeout(t);
      t = setTimeout(() => whatsappAPI.unread().then(r => mounted && setWaUnread({ ...r.data, _t: Date.now() })).catch(() => {}), 300);
    };
    check();
    const iv = setInterval(check, 20000);
    const onVis = () => { if (!document.hidden) check(); };
    window.addEventListener('wa-unread-refresh', check);
    document.addEventListener('visibilitychange', onVis);
    return () => { mounted = false; clearTimeout(t); clearInterval(iv); window.removeEventListener('wa-unread-refresh', check); document.removeEventListener('visibilitychange', onVis); };
  }, [canWa]);

  // V71.51 — Pazarlama rozeti: 'Yeni' ITINERA talebi sayısı (60 sn yoklama + sayfada durum değişince anında)
  const canItinera = hasPermission('itinera_leads', 'view');
  const [itineraYeni, setItineraYeni] = useState(0);
  useEffect(() => {
    if (!canItinera) return undefined;
    let mounted = true;
    const check = () => itineraLeadsAPI.count().then(r => mounted && setItineraYeni(r.data?.yeni || 0)).catch(() => {});
    check();
    const iv = setInterval(check, 60000);
    const onVis = () => { if (!document.hidden) check(); };
    window.addEventListener('itinera-count-refresh', check);
    document.addEventListener('visibilitychange', onVis);
    return () => { mounted = false; clearInterval(iv); window.removeEventListener('itinera-count-refresh', check); document.removeEventListener('visibilitychange', onVis); };
  }, [canItinera]);

  // V71.31.2 — asistan devri bekleyen konuşma: Müşteri Hattı yanıp söner + ses (yeni devirde ve bekledikçe dakikada bir)
  const devirRef = useRef({ ids: new Set(), son: 0 });
  useEffect(() => {
    const d = waUnread?.devir || [];
    const r = devirRef.current;
    const yeni = d.some(x => !r.ids.has(x.id));
    r.ids = new Set(d.map(x => x.id));
    if (d.length && (yeni || Date.now() - r.son > 60000)) { devirSesi(d.some(x => x.acil)); r.son = Date.now(); }
  }, [waUnread]);
  const devirSayi = waUnread?.devir?.length || 0;
  // V71.31.3 — en acil devrin geri sayımı: sunucunun kalan süresi + yerel saniye (saat farkından etkilenmez)
  const [anlik, setAnlik] = useState(Date.now());
  useEffect(() => {
    if (!devirSayi) return undefined;
    const iv = setInterval(() => setAnlik(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [devirSayi]);
  const enAcil = waUnread?.devir?.[0] || null;
  const kalan = enAcil ? Math.round(enAcil.kalan_sn - (anlik - (waUnread._t || anlik)) / 1000) : 0; // V71.31.4 — hiç durmaz
  const gecti = !!enAcil && kalan < 0;
  const devirAcil = !!waUnread?.devir?.some(x => x.acil) || gecti;
  const iki = (n) => String(n).padStart(2, '0');
  const sureYaz = (sn) => { const a = Math.abs(sn); return a >= 3600 ? `${Math.floor(a / 3600)}:${iki(Math.floor(a / 60) % 60)}:${iki(a % 60)}` : `${Math.floor(a / 60)}:${iki(a % 60)}`; };
  const hedefYaz = enAcil?.hedef ? new Date(enAcil.hedef).toLocaleString('tr-TR', { weekday: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }) : '';
  const geriRenk = !enAcil ? null : gecti ? '#dc2626' : `hsl(${Math.round(120 * Math.max(0, Math.min(1, kalan / (enAcil.sure_sn || 300))))}, 75%, 40%)`;

  // V70.65 — Sidebar sadeleşti: alt menüler kaldırıldı, her grup TEK satır.
  // Grubun sayfaları arası geçiş, sayfa üstündeki yatay sekme şeridinden (PageGroupTabs) yapılır.
  // Grup içerikleri tek kaynaktan: config/pageGroups.js
  const navConfig = [
    { type: 'link', to: '/', label: 'Dashboard', icon: icons.home, end: true, grad: ['#4f46e5', '#818cf8'] },
    { type: 'link', to: '/ops-map', label: 'Operasyon Haritası', icon: icons.map, perm: ['ops_map', 'view'], grad: ['#302880', '#3b82f6'] }, // V71.16
    { type: 'link', to: '/customers', label: 'Yolcu Listesi', icon: icons.users, perm: ['customers', 'view'], grad: ['#0891b2', '#22d3ee'] },
    { type: 'link', to: '/companies', label: 'Firma Yönetimi', icon: icons.building, perm: ['companies', 'view'], grad: ['#047857', '#10b981'] }, // V71.26 — Finans & Muhasebe grubu kapandı
    { type: 'link', to: '/whatsapp', label: 'Müşteri Hattı', icon: icons.chat, perm: ['whatsapp', 'view'], grad: ['#15803d', '#25d366'] }, // V71.11 — WhatsApp masası
    { type: 'link', to: '/rezervasyonlar', label: 'Rezervasyonlar', icon: icons.bookmark, perm: ['rezervasyon_takip', 'view'], grad: ['#7c3aed', '#a78bfa'] }, // V71.24 — uçak + otel rezervasyon takibi (ayrı yetki)
    // V71 — Döviz Çevirici tek giriş: Hızlı Araçlar (sağ kenardaki FAB) → Kur Çevirici; /currency rotası duruyor

    { type: 'divider' },

    // V70.136 — grup sırası kullanıcı isteğiyle: 1 uçak, 2 otel, 3 finans, 4 vize, sonrası
    { type: 'group', groupKey: 'operasyon', icon: icons.plane }, // V71.25 — Uçak + Otel Operasyon birleşti
    { type: 'group', groupKey: 'vize', icon: icons.passport },
    { type: 'group', groupKey: 'pazarlama', icon: icons.target },
    // V70.55 — İK Portalı menü satırı DEĞİL: user alanının üstünde gradyanlı özel CTA (aşağıda).

    { type: 'divider' },
    { type: 'link', to: '/shared-files', label: 'Dosya Paylaşım', icon: icons.upload, perm: ['shared_files', 'view'], grad: ['#0d9488', '#2dd4bf'] },
    { type: 'link', to: '/rehberim', label: 'Rehberim', icon: icons.phone, grad: ['#0f766e', '#14b8a6'] }, // V71.9 — santral arayan adı
    { type: 'link', to: '/haberler', label: 'Sektör Haberleri', icon: icons.news, grad: ['#0369a1', '#38bdf8'] }, // V71.32
    // V71 — admin olmayan yetkililer için giriş noktası (admin bu sayfalara Ayarlar kartlarından ulaşır)
    { type: 'link', to: '/announcements', label: 'Duyurular', icon: icons.bookmark, perm: ['announcements', 'view'], nonAdmin: true, grad: ['#4f46e5', '#818cf8'] },
    { type: 'link', to: '/transfers', label: 'Aktarımlar', icon: icons.file, permAny: [['customers', 'create'], ['tickets', 'create'], ['companies', 'create']], nonAdmin: true, grad: ['#334155', '#64748b'] },
    { type: 'group', groupKey: 'sistem', icon: icons.shield },
  ];

  // V71 — İK Portalı CTA: HRLayout.portalAccess ile birebir aynı kural (registry'den türetilir;
  // elle kopyalanmış anahtar listesi kaldırıldı)
  const canSeeHRPortal = canEnterHRPortal(hasPermission, isAdmin, hrEngine);

  const handleNavClick = () => {
    if (isMobile) setMobileOpen(false);
  };

  // V70.65 — aktif grup: sekme şeridindeki herhangi bir sayfadayken grup linki aktif görünür
  // V71 — şeritsiz yönetim sayfalarında da (bildirim ayarları, MIO Sync…) Sistem vurgulanır
  const activeGroupKey = findSidebarGroupKey(location.pathname);

  const initials = user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?';
  const roleName = user?.role === 'admin' ? 'Admin' : 'Kullanıcı';
  const mini = collapsed && !isMobile;

  return (
    <>
      {isMobile && mobileOpen && (
        <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} />
      )}
      <aside className={`sidebar ${mini ? 'collapsed' : ''} ${isMobile ? 'sidebar-mobile' : ''} ${isMobile && mobileOpen ? 'sidebar-mobile-open' : ''}`}>
        {/* Brand */}
        <Link to="/" className="sb-brand" onClick={handleNavClick} aria-label="Dashboard">
          {mini ? (
            <div className="brand-logo-mini">M</div>
          ) : (
            <div className="brand-logo">
              <img src="/mtu-hub-2026-light.png" alt="MTU AgentHUB" className="brand-logo-img" />
            </div>
          )}
        </Link>

        {/* Nav */}
        <nav className="sb-nav">
          {navConfig.map((item, idx) => {
            if (item.type === 'divider') {
              return <div key={`div-${idx}`} className="sidebar-divider" />;
            }

            if (item.type === 'link') {
              if ((item.nonAdmin && isAdmin) || !canSeeGroupItem(item, hasPermission, isAdmin)) return null;
              return (
                <NavLink key={item.to} to={item.to} end={item.end}
                  className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}${item.to === '/whatsapp' && devirSayi ? ` sb-devir${devirAcil ? ' acil' : ''}` : ''}`}
                  title={item.to === '/whatsapp' && devirSayi ? `Asistan devri bekliyor: ${devirSayi} konuşma · en acil: ${gecti ? `${sureYaz(kalan)} gecikti` : `${sureYaz(kalan)} kaldı`} · hedef ${hedefYaz}${enAcil.mesai_disi ? ' (mesai dışı: ilk iş günü)' : ''}` : mini ? item.label : undefined}
                  onClick={handleNavClick}>
                  <IconChip icon={item.icon} grad={item.grad} />
                  <span className="nav-label">{item.label}</span>
                  {item.to === '/whatsapp' && devirSayi > 0 && (
                    <span className={`sb-count sb-geri${gecti ? ' gecti' : ''}`} style={{ background: geriRenk }}>
                      {enAcil.mesai_disi ? '🌙' : '⏱'} {gecti ? '+' : ''}{sureYaz(kalan)}
                    </span>
                  )}
                  {item.to === '/whatsapp' && !devirSayi && waUnread?.konusma > 0 && (
                    <span className="sb-count" title={`${waUnread.konusma} okunmamış konuşma · ${waUnread.mesaj} yeni mesaj`}>
                      {waUnread.mesaj > 99 ? '99+' : waUnread.mesaj}
                    </span>
                  )}
                </NavLink>
              );
            }

            // V70.65 — grup: tek satır; tıklayınca grubun görülebilen ilk sayfası açılır,
            // sayfalar arası geçiş üstteki yatay sekme şeridinden yapılır
            if (item.type === 'group') {
              const group = PAGE_GROUPS.find(g => g.key === item.groupKey);
              if (!group || (group.adminOnly && !isAdmin)) return null;
              const visible = group.items.filter(c => canSeeGroupItem(c, hasPermission, isAdmin));
              if (!visible.length) return null;
              const isActive = activeGroupKey === group.key;
              // V71.31.4 — Operasyon: uçak + otel API tek nokta (hepsi açık yeşil, biri kapalı sarı, hepsi kapalı kırmızı)
              const opsApi = group.key === 'operasyon' ? [
                huStatus && visible.some(c => c.dept === 'ucak') && ['Uçak', huStatus],
                hotelStatus && visible.some(c => c.dept === 'otel') && ['Otel', hotelStatus],
              ].filter(Boolean) : [];
              const opsOk = opsApi.filter(([, st]) => st === 'ok').length;
              return (
                <NavLink key={group.key} to={visible[0].to}
                  className={() => `sidebar-link ${isActive ? 'active' : ''}`}
                  title={mini ? group.label : undefined}
                  onClick={handleNavClick}>
                  <IconChip icon={item.icon} grad={group.color} />
                  <span className="nav-label">{group.label}</span>
                  {opsApi.length > 0 && (
                    <span className={`sidebar-live-dot ${opsOk === opsApi.length ? 'live-ok' : opsOk ? 'live-warn' : 'live-err'}`}
                      title={opsApi.map(([n, st]) => `${n} API ${st === 'ok' ? 'aktif' : 'bağlantı hatası'}`).join(' · ')} />
                  )}
                  {group.key === 'pazarlama' && itineraYeni > 0 && (
                    <span className="sb-count" title={`${itineraYeni} yeni ITINERA talebi`}>{itineraYeni > 99 ? '99+' : itineraYeni}</span>
                  )}
                </NavLink>
              );
            }

            return null;
          })}
        </nav>

        {/* V71.13.10 — Fikrim Var: yüzen buton yerine menüde */}
        <IdeaWidget mini={mini} />

        {/* V70.55 — İK Portalı: gradyanlı özel giriş, user alanının üstünde */}
        {canSeeHRPortal && (
          <Link
            to="/HR"
            className={`sb-hr-cta${mini ? ' mini' : ''}`}
            onClick={handleNavClick}
            title="İK Portalı — izin, avans, harcama ve İK yönetimi"
          >
            <span className="sb-hr-cta-ic">İK</span>
            {!mini && (
              <>
                <div className="sb-hr-cta-text">
                  <div className="sb-hr-cta-title">İK Portalı</div>
                  <div className="sb-hr-cta-sub">İzin · Avans · Harcama</div>
                </div>
                <span className="sb-hr-cta-arrow">→</span>
              </>
            )}
          </Link>
        )}

        {/* User card — daraltılmışken yalnız avatar */}
        <div className="sb-foot">
          <Link to="/profile" className="sb-user" onClick={handleNavClick} title={mini ? (user?.full_name || 'Profil') : undefined}>
            <div className="sb-ava">{initials}</div>
            <div className="meta">
              <div className="nm">{user?.full_name || '—'}</div>
              <div className="rl">{roleName}</div>
            </div>
          </Link>
        </div>

        {/* Version + daralt/genişlet */}
        <div className="sidebar-version">
          {!mini && (
            <Link to="/changelog" className="sb-version-link" onClick={handleNavClick} title="Değişiklik Geçmişi">
              {VERSION}
            </Link>
          )}
          {!isMobile && (
            <button
              type="button"
              className="sb-collapse-btn"
              onClick={() => setCollapsed(c => !c)}
              title={mini ? 'Menüyü genişlet' : 'Menüyü daralt'}
              aria-label={mini ? 'Menüyü genişlet' : 'Menüyü daralt'}
            >
              {mini ? icons.chevronRight : icons.chevronLeft}
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
